import { useEffect, useRef } from "@wordpress/element";
import { useBlockProps, InnerBlocks } from "@wordpress/block-editor";
import { useSelect, useDispatch } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import classNames from "classnames";

import Settings from "./Settings";

// Sample data for preview
const SAMPLE_CHART = {
  name: "Sample Size Chart",
  columns: ["Size", "Chest (in)", "Waist (in)", "Hip (in)"],
  rows: [
    {
      Size: "XS",
      "Chest (in)": "32-34",
      "Waist (in)": "24-26",
      "Hip (in)": "34-36",
    },
    {
      Size: "S",
      "Chest (in)": "34-36",
      "Waist (in)": "26-28",
      "Hip (in)": "36-38",
    },
    {
      Size: "M",
      "Chest (in)": "36-38",
      "Waist (in)": "28-30",
      "Hip (in)": "38-40",
    },
    {
      Size: "L",
      "Chest (in)": "38-40",
      "Waist (in)": "30-32",
      "Hip (in)": "40-42",
    },
    {
      Size: "XL",
      "Chest (in)": "40-42",
      "Waist (in)": "32-34",
      "Hip (in)": "42-44",
    },
  ],
};

export default function Edit(props) {
  const { attributes, clientId } = props;
  const { displayType } = attributes;
  const hasInitialized = useRef(false);

  const blockProps = useBlockProps({
    className: classNames("brandy-size-chart-editor", {
      "is-popup-mode": displayType === "popup",
    }),
  });

  // Get inner blocks and dispatch functions
  const { innerBlocks } = useSelect(
    (select) => ({
      innerBlocks: select("core/block-editor").getBlocks(clientId),
    }),
    [clientId]
  );

  const { replaceInnerBlocks } = useDispatch("core/block-editor");

  // Create template for button block
  const getButtonTemplate = () => [
    [
      "brandy/button",
      {
        text: "Size chart",
        className: "size-chart-trigger-button",
        lock: {
          remove: true,
          move: true,
        },
        style: {
          color: {
            background: "transparent",
            text: "inherit",
          },
          typography: {
            fontStyle: "normal",
            fontWeight: "400",
          },
          shadow: "none",
          hoverShadow: "none",
          spacing: {
            padding: {
              top: "0",
              right: "0",
              bottom: "0",
              left: "0",
            },
          },
          border: {
            radius: "0",
            width: "0",
            color: "inherit",
            style: "solid",
          },
        },
        showText: true,
        icon: {
          enabled: true,
          name: "ruler",
          type: "regular",
          position: "left",
          gap: "5px",
        },
      },
    ],
  ];

  // Function to add button block when displayType is popup
  const addButtonBlock = () => {
    const template = getButtonTemplate();
    const buttonBlock = wp.blocks.createBlock(template[0][0], template[0][1]);

    if (!innerBlocks || innerBlocks.length === 0) {
      replaceInnerBlocks(clientId, [buttonBlock], false);
    } else {
      // Check if button block already exists
      const hasButtonBlock = innerBlocks.some(
        (block) => block.name === "brandy/button"
      );

      if (!hasButtonBlock) {
        replaceInnerBlocks(clientId, [buttonBlock, ...innerBlocks], false);
      }
    }
  };

  // Function to ensure button block exists and restore it if removed
  const ensureButtonBlockExists = () => {
    if (displayType === "popup" && innerBlocks) {
      const hasButtonBlock = innerBlocks.some(
        (block) => block.name === "brandy/button"
      );

      if (!hasButtonBlock) {
        // Button was removed, restore it immediately
        addButtonBlock();
      }
    }
  };

  // Function to remove button block when displayType is table
  const removeButtonBlock = () => {
    if (innerBlocks && innerBlocks.length > 0) {
      const filteredBlocks = innerBlocks.filter(
        (block) => block.name !== "brandy/button"
      );
      replaceInnerBlocks(clientId, filteredBlocks, false);
    }
  };

  // Effect to handle displayType changes
  useEffect(() => {
    if (hasInitialized.current) {
      if (displayType === "popup") {
        addButtonBlock();
      } else {
        removeButtonBlock();
      }
    }
  }, [displayType]);

  // Effect to monitor inner blocks and ensure button exists when displayType is popup
  useEffect(() => {
    if (hasInitialized.current) {
      ensureButtonBlockExists();
    }
  }, [innerBlocks, displayType]);

  // Initialize the block
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      if (displayType === "popup") {
        addButtonBlock();
      }
    }
  }, []);

  const renderSampleChart = () => (
    <div className="brandy-size-chart">
      <div className="brandy-size-chart__header">
        <h3 className="brandy-size-chart__title">{SAMPLE_CHART.name}</h3>
      </div>
      <div className="brandy-size-chart__content">
        <div className="brandy-size-chart__table-wrapper">
          <table className="brandy-size-chart__table">
            <thead>
              <tr>
                {SAMPLE_CHART.columns.map((column, index) => (
                  <th key={index}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SAMPLE_CHART.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {SAMPLE_CHART.columns.map((column, colIndex) => (
                    <td key={colIndex}>{row[column] || ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Settings attributes={attributes} setAttributes={props.setAttributes} />
      <div {...blockProps}>
        {displayType === "popup" ? (
          <div className="size-chart-popup-editor">
            <InnerBlocks template={getButtonTemplate()} />
            <div className="size-chart-preview-note">
              <p style={{ fontSize: "12px", color: "#666", marginTop: "10px" }}>
                {__(
                  "👆 This button will open the size chart in a popup on the frontend",
                  "brandy-blocks"
                )}
              </p>
            </div>
          </div>
        ) : (
          renderSampleChart()
        )}
      </div>
    </>
  );
}
